import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  Heading,
  Text,
  Card,
  CardBody,
  Code,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { FiFileText, FiSave } from 'react-icons/fi';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, setDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

// Cast icons as React.ElementType to fix TypeScript issues
const FiFileTextIcon = FiFileText as React.ElementType;
const FiSaveIcon = FiSave as React.ElementType;

export interface AppSettings {
  id: string;
  policyTemplate?: string;
  policyVersion?: number;
  bookingConstraints?: {
    minAdvanceNoticeHours?: number;
    maxAdvanceBookingDays?: number;
    sameDayCutoffHours?: number;
  };
  createdAt: any;
  updatedAt: any;
  updatedBy: string;
}

const DEFAULT_TEMPLATE = `If an appointment is missed, cancelled or changed after the policy deadlines, there can be a charge or cancellation fee.

Changes

Appointments can be changed until {minAdvanceNoticeHours} hours in advance. Appointments can't be rescheduled further than {maxAdvanceBookingDays} days in the future.

Cancellation

Appointments can be cancelled until {sameDayCutoffHours} hours in advance.`;

const MACROS = [
  { macro: '{minAdvanceNoticeHours}', desc: 'Minimum advance notice (hours) for changes' },
  { macro: '{maxAdvanceBookingDays}', desc: 'Maximum days in advance for rescheduling' },
  { macro: '{sameDayCutoffHours}', desc: 'Same day cutoff (hours) for cancellation' },
];

function renderPolicyTemplate(template: string, constraints?: AppSettings['bookingConstraints']): string {
  return template
    .replace(/\{minAdvanceNoticeHours\}/g, (constraints?.minAdvanceNoticeHours ?? 3).toString())
    .replace(/\{maxAdvanceBookingDays\}/g, (constraints?.maxAdvanceBookingDays ?? 30).toString())
    .replace(/\{sameDayCutoffHours\}/g, (constraints?.sameDayCutoffHours ?? 4).toString());
}

const PolicySettings: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [policyVersion, setPolicyVersion] = useState<number>(1);
  const [constraints, setConstraints] = useState<AppSettings['bookingConstraints']>();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        const settings = settingsSnap.data() as AppSettings;
        if (settings.policyTemplate) {
          setTemplate(settings.policyTemplate);
        }
        if (settings.policyVersion !== undefined) {
          setPolicyVersion(settings.policyVersion);
        }
        if (settings.bookingConstraints) {
          setConstraints(settings.bookingConstraints);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load policy settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser) {
      toast({
        title: 'Error',
        description: 'You must be logged in to save settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      const newVersion = (policyVersion || 0) + 1;

      if (settingsSnap.exists()) {
        await updateDoc(settingsRef, {
          policyTemplate: template,
          policyVersion: newVersion,
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      } else {
        await setDoc(settingsRef, {
          id: 'default',
          policyTemplate: template,
          policyVersion: newVersion,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      }

      setPolicyVersion(newVersion);

      toast({
        title: 'Success',
        description: `Policy updated and version incremented to ${newVersion}. All users will be required to re-accept.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save policy settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <Text>Loading policy settings...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <HStack mb={6} align="center">
        <Box
          p={3}
          borderRadius="lg"
          bg="purple.50"
          color="purple.600"
        >
          <FiFileTextIcon size={24} />
        </Box>
        <Heading size="lg">Booking Policy</Heading>
      </HStack>

      <Alert status="warning" mb={6}>
        <AlertIcon />
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Important
          </Text>
          <Text fontSize="sm">
            When you save, the policy version will be incremented. All users (authenticated and unauthenticated) 
            will be required to re-accept the updated policy before they can complete bookings.
          </Text>
        </Box>
      </Alert>

      <VStack spacing={6} align="stretch">
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <HStack mb={2}>
                  <Text fontWeight="semibold">Current Policy Version</Text>
                  <Badge colorScheme="purple" fontSize="md" px={2} py={1}>
                    {policyVersion || 1}
                  </Badge>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  This version will be incremented to {((policyVersion || 0) + 1)} when you save
                </Text>
              </Box>

              <Divider />

              <FormControl>
                <FormLabel>Policy Template</FormLabel>
                <Textarea
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                  rows={10}
                  fontFamily="mono"
                  fontSize="sm"
                  placeholder="Enter your booking policy template..."
                />
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Use the macros below to insert dynamic values from your booking constraints
                </Text>
              </FormControl>

              <Divider />

              <Box>
                <Text fontWeight="semibold" mb={3}>Available Macros:</Text>
                <VStack align="stretch" spacing={2}>
                  {MACROS.map((m) => (
                    <HStack key={m.macro} align="start">
                      <Code colorScheme="purple" fontSize="sm" px={2} py={1}>
                        {m.macro}
                      </Code>
                      <Text fontSize="sm" color="gray.600" flex={1}>
                        {m.desc}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              <Divider />

              <Box>
                <Text fontWeight="semibold" mb={3}>Live Preview:</Text>
                <Card bg="gray.50" borderWidth={1} borderColor="gray.200">
                  <CardBody>
                    <Text
                      fontSize="sm"
                      whiteSpace="pre-wrap"
                      color="gray.800"
                      lineHeight="1.6"
                    >
                      {renderPolicyTemplate(template, constraints)}
                    </Text>
                  </CardBody>
                </Card>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  This is how the policy will appear to users with current booking constraint values
                </Text>
              </Box>
            </VStack>
          </CardBody>
        </Card>

        <HStack justify="flex-end">
          <Button
            colorScheme="purple"
            leftIcon={React.createElement(FiSaveIcon)}
            onClick={handleSave}
            isLoading={saving}
            loadingText="Saving..."
            size="lg"
          >
            Save & Increment Version
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default PolicySettings;

